import { getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import {
  getHandoverNoteBody,
  getHandoverEntryBody,
} from './handoverNoteBody';
import {
  getHandoverPatients,
  normalizeHandoverPatientEntry,
} from './handoverUtils';

function noteNarrative(note, type) {
  if (!note) return '';
  if (type === 'handover') return getHandoverNoteBody(note) || '';
  if (typeof note.narrative === 'string' && note.narrative.trim()) return note.narrative.trim();
  if (typeof note.free_text === 'string' && note.free_text.trim()) return note.free_text.trim();
  if (note.soap) {
    return ['subjective', 'objective', 'assessment', 'plan']
      .map((key) => String(note.soap[key] || '').trim())
      .filter(Boolean)
      .join('\n\n');
  }
  return '';
}

function queueItemPrimaryType(item) {
  return item?.generatedNotes?.find((e) => e.note)?.type || item?.noteType || null;
}

export function summarizeReviewQueuePatients(queue, admittedPatients = [], language = 'en') {
  if (!Array.isArray(queue)) return [];

  return queue.map((item, index) => {
    const rosterPatient = item?.patientId
      ? getPatientById(item.patientId, admittedPatients)
      : null;
    const primary =
      item?.generatedNotes?.find((e) => e.note)?.note
      || item?.openaiNote
      || null;

    return {
      id: item?.patientId || `queue-${index}`,
      name: rosterPatient
        ? getPatientDisplayName(rosterPatient, language)
        : primary?.patient?.name || `Patient ${index + 1}`,
      patient_name: rosterPatient
        ? getPatientDisplayName(rosterPatient, language)
        : primary?.patient?.name || `Patient ${index + 1}`,
      room: rosterPatient?.room || primary?.patient?.room || null,
    };
  });
}

function entryFromQueueItem(item, admittedPatients, language, index) {
  const handover = item?.generatedNotes?.find((e) => e.type === 'handover' && e.note)?.note;
  if (handover) {
    const patients = getHandoverPatients(handover);
    if (patients.length) {
      return patients.map((entry) =>
        normalizeHandoverPatientEntry({
          ...entry,
          narrative: getHandoverEntryBody(entry) || entry.narrative,
        })
      );
    }
  }

  const primary =
    item?.generatedNotes?.find((e) => e.note)?.note
    || item?.openaiNote
    || null;
  const primaryType =
    item?.generatedNotes?.find((e) => e.note)?.type || item?.noteType || 'handover';
  const rosterPatient = item?.patientId
    ? getPatientById(item.patientId, admittedPatients)
    : null;

  return [
    normalizeHandoverPatientEntry({
      patient_name: rosterPatient
        ? getPatientDisplayName(rosterPatient, language)
        : primary?.patient?.name || `Patient ${index + 1}`,
      room: rosterPatient?.room || primary?.patient?.room || null,
      narrative: noteNarrative(primary, primaryType),
    }),
  ];
}

/**
 * Collapse a multi-item review queue into one handover draft with patients[]
 * so every patient renders as its own section on a single scrollable page.
 *
 * Only merges when every queue item is already a handover note. MAR / vitals /
 * progress notes keep their detected types and stay as a sequential review queue.
 */
export function mergeReviewQueueToMultiPatientDraft(queue, admittedPatients = [], language = 'en') {
  if (!Array.isArray(queue) || queue.length < 2) return null;

  const allHandover = queue.every((item) => queueItemPrimaryType(item) === 'handover');
  if (!allHandover) return null;

  const patients = queue.flatMap((item, index) =>
    entryFromQueueItem(item, admittedPatients, language, index)
  ).filter((entry) => entry.patient_name || entry.room || entry.narrative);

  if (patients.length < 2) return null;

  const base = queue[0];
  const sharedTranscript =
    queue.map((item) => item.transcriptCleaned || item.transcriptRaw || '').find((t) => t.trim())
    || '';
  const sharedRaw =
    queue.map((item) => item.transcriptRaw || '').find((t) => t.trim()) || sharedTranscript;

  return {
    ...base,
    patientId: null,
    needsPatientSelection: false,
    noteType: 'handover',
    noteId: `${base.encounterGroupId || base.noteId || 'multi'}-combined`,
    reviewQueue: null,
    reviewQueueIndex: 0,
    transcriptRaw: sharedRaw,
    transcriptCleaned: sharedTranscript,
    generatedNotes: [
      {
        type: 'handover',
        note: {
          note_type: 'handover',
          datetime: base.encounterDateTime || new Date().toISOString(),
          patients,
          flags: [],
        },
        error: null,
      },
    ],
    openaiNote: {
      note_type: 'handover',
      datetime: base.encounterDateTime || new Date().toISOString(),
      patients,
      flags: [],
    },
    companionVitalsNote: null,
    cortiNote: null,
    cortiFailed: true,
  };
}
